
import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ReviewType } from "@/components/course/review-management/types";

export const useReviewsManagement = (courseId: string) => {
  const queryClient = useQueryClient();
  const [deleteReviewId, setDeleteReviewId] = useState<string | null>(null);
  const [selectedReviews, setSelectedReviews] = useState<string[]>([]);
  const [reviewState, setReviewState] = useState<ReviewType[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const getAccessToken = async () => {
      const { data } = await supabase.auth.getSession();
      setAccessToken(data.session?.access_token || null);
    };
    
    getAccessToken();
  }, []);

  const toggleReviewMutation = useMutation({
    mutationFn: async ({ 
      reviewId, 
      courseId, 
      isFeatured, 
      isPublic 
    }: { 
      reviewId: string, 
      courseId: string, 
      isFeatured?: boolean, 
      isPublic?: boolean 
    }) => {
      console.log('[ToggleReview] Mutation called with:', { reviewId, courseId, isFeatured, isPublic });
      
      if (!accessToken) {
        throw new Error("No access token available");
      }
      
      const response = await fetch(`https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          path: 'update_review',
          params: { 
            review_id: reviewId, 
            course_id: courseId, 
            is_featured: isFeatured, 
            is_public: isPublic 
          }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("[ToggleReview] HTTP Error:", response.status, errorText);
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log("[ToggleReview] Success response:", data);
      
      return { reviewId, isFeatured, isPublic };
    },
    onSuccess: (data) => {
      setReviewState(currentReviews => 
        currentReviews.map(review => {
          if (review.id === data.reviewId) {
            return {
              ...review,
              ...(data.isFeatured !== undefined && { is_featured: data.isFeatured }),
              ...(data.isPublic !== undefined && { is_public: data.isPublic })
            };
          }
          return review;
        })
      );
      
      queryClient.invalidateQueries({ queryKey: ['course-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-featured-reviews', courseId] });
      
      if (data.isFeatured !== undefined) {
        toast.success(`Review ${data.isFeatured ? 'featured' : 'unfeatured'}`);
      }
      if (data.isPublic !== undefined) {
        toast.success(`Review ${data.isPublic ? 'shown' : 'hidden'}`);
      }
    },
    onError: (error) => {
      console.error('[ToggleReview] Error:', error);
      toast.error('Failed to update review');
    }
  });

  const { data: reviews, isLoading } = useQuery({
    queryKey: ['course-reviews', courseId],
    queryFn: async () => {
      try {
        console.log("[ReviewsQuery] Fetching reviews for course:", courseId);
        
        if (!accessToken) {
          console.warn("[ReviewsQuery] No access token available");
          return [];
        }
        
        const response = await fetch(`https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            path: 'get_course_reviews',
            params: { course_id: courseId }
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error("[ReviewsQuery] HTTP Error:", response.status, errorText);
          throw new Error(`Error ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        return data || [];
      } catch (error) {
        console.error("[ReviewsQuery] Exception:", error);
        toast.error("Failed to load reviews");
        return [];
      }
    },
    enabled: !!accessToken
  });

  useEffect(() => {
    if (reviews) {
      setReviewState(reviews);
    }
  }, [reviews]);

  const updateReviewVisibilityMutation = useMutation({
    mutationFn: async ({ reviewId, isPublic }: { reviewId: string, isPublic: boolean }) => {
      console.log(`[UpdateVisibility] Setting review ${reviewId} visibility to ${isPublic}`);
      
      if (!accessToken) throw new Error("No access token available");
      
      const response = await fetch(`https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          path: 'update_review_visibility',
          params: { review_id: reviewId, is_public: isPublic }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[UpdateVisibility] HTTP Error: ${response.status}`, errorText);
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log(`[UpdateVisibility] Success response for review ${reviewId}:`, data);
      
      if (data.error) {
        console.error(`[UpdateVisibility] API returned error:`, data.error);
        throw new Error(data.error);
      }
      
      return { reviewId, isPublic, data };
    },
    onSuccess: (data) => {
      setReviewState(currentReviews => 
        currentReviews.map(review => 
          review.id === data.reviewId 
            ? { ...review, is_public: data.isPublic } 
            : review
        )
      );
      
      queryClient.invalidateQueries({ queryKey: ['course-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-featured-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'all', courseId] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'featured', courseId] });
      
      toast.success(`Review ${data.isPublic ? 'shown to students' : 'hidden from students'}`);
    },
    onError: (error: Error) => {
      console.error('[UpdateVisibility] Error:', error);
      toast.error(`Failed to update review visibility: ${error.message}`);
    }
  });

  const updateReviewFeaturedMutation = useMutation({
    mutationFn: async ({ reviewId, isFeatured }: { reviewId: string, isFeatured: boolean }) => {
      console.log(`[UpdateFeatured] Setting review ${reviewId} featured status to ${isFeatured}`);
      
      if (!accessToken) {
        throw new Error("No access token available");
      }
      
      try {
        if (isFeatured) {
          console.log("[UpdateFeatured] Ensuring review is public before featuring");
          const visibilityResponse = await fetch(`https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              path: 'update_review_visibility',
              params: { review_id: reviewId, is_public: true }
            })
          });
          
          if (!visibilityResponse.ok) {
            console.error("[UpdateFeatured] Failed to set review public:", await visibilityResponse.text());
          } else {
            console.log("[UpdateFeatured] Successfully set review public:", reviewId);
          }
        }
        
        const response = await fetch(`https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            path: 'update_review_featured',
            params: { 
              review_id: reviewId, 
              is_featured: isFeatured,
              course_id: courseId
            }
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error("[UpdateFeatured] HTTP Error:", response.status, errorText);
          throw new Error(`Error ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        console.log("[UpdateFeatured] Success response:", data);
        
        if (data.error) {
          console.error("[UpdateFeatured] API returned error:", data.error);
          throw new Error(data.error);
        }
        
        return { reviewId, isFeatured, data };
      } catch (error) {
        console.error("[UpdateFeatured] Fetch error:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log(`[UpdateFeatured] onSuccess: Updated featured status for review ${data.reviewId} to ${data.isFeatured}`);
      
      setReviewState(currentReviews => 
        currentReviews.map(review => 
          review.id === data.reviewId 
            ? { ...review, is_featured: data.isFeatured, is_public: data.isFeatured ? true : review.is_public }
            : review
        )
      );
      
      queryClient.invalidateQueries({ queryKey: ['course-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-featured-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'all', courseId] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'featured', courseId] });
      
      toast.success(`Review ${data.isFeatured ? 'featured' : 'unfeatured'}`);
    },
    onError: (error: Error) => {
      console.error('[UpdateFeatured] Error:', error.message);
      toast.error(`Failed to update featured status: ${error.message}`);
    }
  });

  const deleteReviewMutation = useMutation({
    mutationFn: async (reviewId: string) => {
      if (!accessToken) throw new Error("No access token available");
      
      const response = await fetch(`https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          path: 'delete_review',
          params: { review_id: reviewId }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      return reviewId;
    },
    onSuccess: (reviewId) => {
      setReviewState(currentReviews => 
        currentReviews.filter(review => review.id !== reviewId)
      );
      
      queryClient.invalidateQueries({ queryKey: ['course-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-average-rating', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-featured-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-review-count', courseId] });
      toast.success('Review deleted successfully');
      setDeleteReviewId(null);
    },
    onError: () => {
      toast.error('Failed to delete review');
      setDeleteReviewId(null);
    }
  });

  const handlePublicVisibilityToggle = (reviewId: string) => {
    const review = reviewState.find(r => r.id === reviewId);
    if (!review) {
      toast.error("Review not found");
      return;
    }
    
    toggleReviewMutation.mutate({
      reviewId, 
      courseId,
      isPublic: !review.is_public
    });
  };

  const handleFeaturedToggle = (reviewId: string) => {
    const review = reviewState.find(r => r.id === reviewId);
    if (!review) {
      toast.error("Review not found");
      return;
    }
    
    toggleReviewMutation.mutate({
      reviewId, 
      courseId,
      isFeatured: !review.is_featured,
      isPublic: !review.is_featured ? true : undefined
    });
  };

  const handleBulkFeature = async () => {
    if (selectedReviews.length === 0) return;
    
    console.log(`[BulkFeature] Featuring ${selectedReviews.length} reviews`, selectedReviews);
    
    try {
      const { data, error } = await supabase
        .from('course_reviews')
        .update({ 
          is_featured: true,
          is_public: true,
          course_id: courseId
        })
        .in('id', selectedReviews)
        .select();

      if (error) {
        console.error('[BulkFeature] Error:', error);
        throw error;
      }
      
      console.log('[BulkFeature] Success response:', data);
      
      setReviewState(currentReviews => 
        currentReviews.map(review => 
          selectedReviews.includes(review.id) 
            ? { ...review, is_featured: true, is_public: true } 
            : review
        )
      );

      queryClient.invalidateQueries({ queryKey: ['course-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-featured-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'all', courseId] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'featured', courseId] });
      
      toast.success(`${selectedReviews.length} reviews featured`);
      setSelectedReviews([]);
    } catch (error) {
      console.error('[BulkFeature] Exception:', error);
      toast.error('Failed to update featured status');
    }
  };

  return {
    reviewState,
    isLoading,
    selectedReviews,
    deleteReviewId,
    accessToken,
    setSelectedReviews,
    setDeleteReviewId,
    handlePublicVisibilityToggle,
    handleFeaturedToggle,
    handleBulkFeature,
    deleteReviewMutation,
    toggleReviewMutation
  };
};
